import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  Shield, 
  UserCheck,
  Calendar,
  MessageSquare,
  Star,
  MapPin
} from "lucide-react";


export interface Expert {
  id: string;
  name: string;
  title?: string;
  specialty: 'business_law' | 'immigration_law' | 'cpa' | 'notary' | 'tax_advisor' | string;
  email?: string;
  phone?: string;
  bio?: string;
  avatarUrl?: string;
  hourlyRate?: number;
  currency?: string;
  availability?: string;
  isVerified?: boolean;
  rating?: number;
  reviewCount?: number;
  jurisdictions?: string[];
  stateProvince?: string;
  serviceRegion?: string;
}

interface ExpertsTabProps {
  experts: Expert[];
  language: 'en' | 'fr';
  userRegion?: string;
  formatCurrency?: (amount: number, currency?: string) => string;
  onBookConsultation?: (expertId: string) => void;
  onMessage?: (expertId: string) => void;
}


const translations = {
  en: {
    ourExperts: "Our Experts",
    acquizExperts: "AcquizGroup's verified internal experts ready to assist you",
    noExpertsAvailable: "No experts available at the moment",
    rate: "Rate",
    availability: "Availability",
    rating: "Rating",
    reviews: "Reviews",
    bookConsultation: "Book Consultation",
    message: "Message",
    contact: "Contact",
    expertCategories: "Expert Categories",
    browseExperts: "Browse experts by specialty",
    businessLaw: "Business Law",
    immigrationLaw: "Immigration Law",
    cpaServices: "CPA Services",
    notaryPublic: "Notary Public",
    taxAdvisory: "Tax Advisory",
    experts: "Experts",
    verified: "Verified",
    otherRegions: "Other regions",
    servesAllRegions: "All regions",
    inYourRegion: "In your region"
  },
  fr: {
    ourExperts: "Nos Experts",
    acquizExperts: "Experts internes vérifiés d'AcquizGroup prêts à vous assister",
    noExpertsAvailable: "Aucun expert disponible pour le moment",
    rate: "Tarif",
    availability: "Disponibilité",
    rating: "Note",
    reviews: "Avis",
    bookConsultation: "Réserver une consultation",
    message: "Message",
    contact: "Contact",
    expertCategories: "Catégories d'Experts",
    browseExperts: "Parcourir par spécialité",
    businessLaw: "Droit des affaires",
    immigrationLaw: "Droit de l'immigration",
    cpaServices: "Services CPA",
    notaryPublic: "Notaire",
    taxAdvisory: "Conseil fiscal",
    experts: "Experts",
    verified: "Vérifié",
    otherRegions: "Autres régions",
    servesAllRegions: "Toutes régions",
    inYourRegion: "Dans votre région"
  }
};


export function ExpertsTab({
  experts,
  language,
  userRegion,
  formatCurrency,
  onBookConsultation,
  onMessage
}: ExpertsTabProps) {
  const t = translations[language];

  const normalize = (s?: string) => (s || "").trim().toLowerCase();
  const userRegionNorm = normalize(userRegion);

  const matchesRegion = (e: Expert): boolean => {
    if (!userRegionNorm) return false;
    const juris = (e.jurisdictions || []).map(normalize);
    if (juris.includes("all") || juris.includes("toutes")) return true;
    if (juris.includes(userRegionNorm)) return true;
    if (normalize(e.stateProvince) === userRegionNorm) return true;
    if (normalize(e.serviceRegion) === userRegionNorm) return true;
    return false;
  };

  const matching = userRegionNorm ? experts.filter(matchesRegion) : [];
  const others = userRegionNorm ? experts.filter(e => !matchesRegion(e)) : experts;

  const getPrimaryRegion = (e: Expert): string => {
    if (e.serviceRegion) return e.serviceRegion;
    if (e.stateProvince) return e.stateProvince;
    if (e.jurisdictions && e.jurisdictions.length > 0) {
      const j = e.jurisdictions[0];
      if (normalize(j) === "all" || normalize(j) === "toutes") return t.servesAllRegions;
      return j;
    }
    return t.servesAllRegions;
  };



  const getSpecialtyLabel = (specialty: string) => {
    switch (specialty) {
      case 'business_law': return t.businessLaw;
      case 'immigration_law': return t.immigrationLaw;
      case 'cpa': return t.cpaServices;
      case 'notary': return t.notaryPublic;
      case 'tax_advisor': return t.taxAdvisory;
      default: return specialty;
    }
  };

  const handleBookConsultation = (expertId: string) => {
    if (onBookConsultation) {
      onBookConsultation(expertId);
    } else {
      alert(language === 'fr' ? "Demande de consultation envoyée ✅" : "Consultation request sent ✅");
    }
  };

  const handleMessage = (expertId: string) => {
    if (onMessage) {
      onMessage(expertId);
    } else {
      alert(language === 'fr' ? "Message envoyé ✅" : "Message sent ✅");
    }
  };

  const formatRate = (rate: number, currency?: string) => {
    if (formatCurrency) {
      return formatCurrency(rate, currency);
    }
    return `$${rate}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {t.ourExperts}
        </CardTitle>
        <CardDescription>{t.acquizExperts}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {experts.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t.noExpertsAvailable}</p>
          </div>
        ) : (() => {
          const renderExpertCard = (expert: Expert) => {
            const isMatch = userRegionNorm ? matchesRegion(expert) : false;
            return (
              <Card key={expert.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={expert.avatarUrl} />
                      <AvatarFallback className="bg-primary/10 text-primary text-lg">
                        {expert.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg">{expert.name}</CardTitle>
                      <CardDescription>{getSpecialtyLabel(expert.specialty)}</CardDescription>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {expert.isVerified && (
                          <Badge variant="secondary">
                            <Shield className="h-3 w-3 mr-1" />
                            {t.verified}
                          </Badge>
                        )}
                        <Badge variant={isMatch ? "default" : "outline"}>
                          <MapPin className="h-3 w-3 mr-1" />
                          {isMatch ? t.inYourRegion : getPrimaryRegion(expert)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {expert.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-3">{expert.bio}</p>
                  )}

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {expert.hourlyRate && (
                      <div>
                        <p className="text-muted-foreground">{t.rate}</p>
                        <p className="font-semibold">{formatRate(expert.hourlyRate, expert.currency)}/hr</p>
                      </div>
                    )}
                    {expert.availability && (
                      <div>
                        <p className="text-muted-foreground">{t.availability}</p>
                        <p className="font-semibold">{expert.availability}</p>
                      </div>
                    )}
                    {expert.rating && (
                      <div>
                        <p className="text-muted-foreground">{t.rating}</p>
                        <p className="font-semibold flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          {expert.rating}/5
                        </p>
                      </div>
                    )}
                    {expert.reviewCount !== undefined && (
                      <div>
                        <p className="text-muted-foreground">{t.reviews}</p>
                        <p className="font-semibold">{expert.reviewCount}</p>
                      </div>
                    )}
                  </div>

                  {(expert.email || expert.phone) && (
                    <div className="text-sm">
                      <p className="text-muted-foreground">{t.contact}:</p>
                      {expert.email && <p>{expert.email}</p>}
                      {expert.phone && <p>{expert.phone}</p>}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t">
                    <Button className="flex-1" onClick={() => handleBookConsultation(expert.id)}>
                      <Calendar className="h-4 w-4 mr-2" />
                      {t.bookConsultation}
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => handleMessage(expert.id)}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t.message}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          };

          return (
            <div className="space-y-6">
              {matching.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matching.map(renderExpertCard)}
                </div>
              )}
              {matching.length > 0 && others.length > 0 && (
                <div className="flex items-center gap-3 py-2">
                  <Separator className="flex-1" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {t.otherRegions}
                  </span>
                  <Separator className="flex-1" />
                </div>
              )}
              {others.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {others.map(renderExpertCard)}
                </div>
              )}
            </div>
          );
        })()}


        {/* Expert Categories */}
        <Card className="mt-6 bg-muted/30">
          <CardHeader>
            <CardTitle className="text-base">{t.expertCategories}</CardTitle>
            <CardDescription>{t.browseExperts}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: 'business_law', label: t.businessLaw },
                { key: 'immigration_law', label: t.immigrationLaw },
                { key: 'cpa', label: t.cpaServices },
                { key: 'notary', label: t.notaryPublic },
                { key: 'tax_advisor', label: t.taxAdvisory }
              ].map(category => (
                <Button key={category.key} variant="outline" className="h-auto py-3 flex flex-col">
                  <span className="font-semibold text-xs">{category.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {experts.filter(e => e.specialty === category.key).length} {t.experts}
                  </span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
